import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { CAP } from '../utils/permissions';
import { usePermissions } from '../utils/PermissionContext';

const WorkspaceCard = ({ item, onAddToCart, t }) => {
  const { can } = usePermissions();
  const canPurchase = can(CAP.PURCHASE);
  const [quantity, setQuantity] = useState('');

  const handleQuantityChange = (value) => {
    const num = parseInt(value, 10);
    setQuantity(value === '' ? '' : (isNaN(num) || num < 1 ? 1 : num));
  };

  const handleAddToCart = () => {
    if (item.price === 0 || Number(item.price) === 0) {
      toast.error(t.contact_admin_to_update_price || "Neem contact op met de beheerder om de prijs bij te werken.", {
        id: `price-error-${item.id}`,
        duration: 4000
      });
      return;
    }
    const q = parseInt(quantity, 10);
    // If quantity is not specified or invalid, default to 1
    const finalQuantity = isNaN(q) || q <= 0 ? 1 : q;

    onAddToCart({
      id: item.id,
      name: item.name,
      skuId: item.related_licenses_sku,
      type: 'license',
      planName: 'ANNUAL_YEARLY_PAY',
      isRenewal: false,
      price: item.price,
      quantity: finalQuantity,
      related_licenses_sku: item.related_licenses_sku || '',
      afasArticleCode: item.afas_artikelcode || ''
    });
    toast.success(t.added_to_cart || "Licenses added to cart!");
    setQuantity('');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200 overflow-hidden transform hover:-translate-y-1 transition duration-300 flex flex-col h-full">
      {/* Decorative top gradient banner */}
      <div className="h-2 bg-gradient-to-r from-primary via-indigo-500 to-purple-600"></div>

      <div className="p-6 flex flex-col flex-grow justify-between space-y-4">
        {/* Header Section */}
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0 flex-grow flex items-center gap-2.5">
            <img src="/google-logo.png" alt="Google" className="w-6 h-6 object-contain shrink-0" />
            <h3 className="text-xl font-bold text-gray-900 truncate" title={item.name}>
              {item.name}
            </h3>
          </div>
        </div>

        {/* Gray specifications detail block */}
        <div className="rounded-xl p-3 border bg-gray-50 border-gray-100 text-gray-800 flex flex-col space-y-2.5">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider block text-gray-400">
                {t.unit_price || 'Price'}
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-base font-extrabold text-gray-800">
                  € {item.price ? Number(item.price).toFixed(2) : "0.00"}
                </span>
                {(item.price === 0 || Number(item.price) === 0) && (
                  <span
                    className="inline-flex items-center justify-center bg-amber-100 text-amber-800 border border-amber-200 rounded-full h-5 w-5 font-extrabold text-xs cursor-help select-none animate-pulse shrink-0"
                    title={t.contact_admin_to_update_price || "Neem contact op met de beheerder om de prijs bij te werken."}
                  >
                    !
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase tracking-wider block text-gray-400">
                {t.related_sku || 'Related SKU'}
              </span>
              <span className="text-xs font-mono font-bold text-gray-600 block bg-white px-2 py-0.5 rounded border border-gray-200">
                {item.related_licenses_sku || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Quantity selection and Add to Cart Button. Read-only accounts hold
            no `purchase` capability and the Dashboard hides the cart for them,
            so the whole row goes rather than leaving a quantity box with
            nowhere to send it. */}
        {canPurchase && <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider select-none">
              {t.quantity || 'Quantity'}:
            </span>
            <input
              type="number"
              min="1"
              placeholder="1"
              className="w-16 border border-gray-300 rounded-md p-1.5 focus:ring-primary focus:border-primary text-sm text-center font-semibold"
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
            />
          </div>
          <button
            onClick={handleAddToCart}
            className="bg-primary hover:bg-purple-800 text-white p-2.5 rounded-xl transition-all duration-200 shrink-0 shadow-sm flex items-center justify-center hover:scale-105 active:scale-95"
            title={t.add_to_cart || 'Add to cart'}
          >
            <svg className="w-5 h-5" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M156.25 468.751C173.509 468.751 187.5 454.76 187.5 437.501C187.5 420.242 173.509 406.251 156.25 406.251C138.991 406.251 125 420.242 125 437.501C125 454.76 138.991 468.751 156.25 468.751Z" fill="currentColor" />
              <path d="M375 468.751C392.259 468.751 406.25 454.76 406.25 437.501C406.25 420.242 392.259 406.251 375 406.251C357.741 406.251 343.75 420.242 343.75 437.501C343.75 454.76 357.741 468.751 375 468.751Z" fill="currentColor" />
              <path d="M437.5 109.376H90.9375L78.125 43.7506C77.3945 40.1682 75.4309 36.9554 72.5759 34.6714C69.7209 32.3874 66.1554 31.1769 62.5 31.2506H0V62.5006H49.6875L109.375 362.501C110.105 366.083 112.069 369.296 114.924 371.58C117.779 373.864 121.345 375.074 125 375.001H406.25V343.751H137.812L125 281.251H406.25C409.862 281.339 413.393 280.173 416.242 277.951C419.09 275.729 421.081 272.588 421.875 269.063L453.125 128.438C453.649 126.12 453.636 123.713 453.089 121.4C452.542 119.087 451.475 116.929 449.968 115.091C448.461 113.253 446.555 111.782 444.395 110.792C442.234 109.802 439.876 109.317 437.5 109.376ZM393.75 250.001H119.062L97.1875 140.626H417.969L393.75 250.001Z" fill="currentColor" />
            </svg>
          </button>
        </div>}
      </div>
    </div>
  );
};

const WorkspaceGrid = ({ workspaces, subscriptions, onAddToCart, t }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const visibleWorkspaces = (workspaces || []).filter(item => {
    const isVisible = item.visible !== false &&
      item.visible !== 'false' &&
      item.visible !== 0 &&
      item.visible !== '0';
    const isPriceZero = item.price === 0 || Number(item.price) === 0 || !item.price;
    const isAfasEmpty = !item.afas_artikelcode || item.afas_artikelcode.toString().trim() === '';

    // Check if there is an active subscription for this SKU with an Annual plan
    const hasActiveAnnualSub = (subscriptions || []).some(sub =>
      sub.skuId === item.related_licenses_sku &&
      (sub.planName || '').toUpperCase().includes('ANNUAL')
    );

    return isVisible && !isPriceZero && !isAfasEmpty && !hasActiveAnnualSub;
  });

  if (visibleWorkspaces.length === 0) {
    return null;
  }

  const filteredWorkspaces = visibleWorkspaces.filter(item =>
    (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.related_licenses_sku || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <img src="/google-logo.png" alt="Google" className="w-8 h-8 object-contain" />
          <h2 className="text-3xl font-bold text-gray-800">
            {t.workspace_plans || 'Workspace Subscription Plans'}
          </h2>
        </div>
        <div className="relative w-full sm:w-80">
          <input
            type="text"
            placeholder={t.search_workspace_plans || "Zoeken..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white shadow-sm"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {filteredWorkspaces.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-base font-semibold">{t.no_search_results || "Geen resultaten gevonden voor jouw zoekopdracht."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkspaces.map(item => (
            <WorkspaceCard
              key={item.id}
              item={item}
              onAddToCart={onAddToCart}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkspaceGrid;
